import {HttpClient, HttpStatusCode} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {UserService} from 'app/core/user/user.service';
import {catchError, from, map, mergeMap, Observable, of, switchMap, tap, throwError} from 'rxjs';
import {HttpService} from "../shared/http.service";
import {NzMessageService} from "ng-zorro-antd/message";
import {Router} from "@angular/router";

@Injectable({providedIn: 'root'})
export class AuthService {
    private _authenticated: boolean = false;
    private _httpClient = inject(HttpClient);
    private _userService = inject(UserService);

    constructor(
        private _http: HttpService,
        private _messageService: NzMessageService,
        private _router: Router
    ) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string) {
        localStorage.setItem('accessToken', token);
    }

    get accessToken(): string {
        return localStorage.getItem('accessToken') ?? '';
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    public removeAccessToken(): void {
        localStorage.removeItem('accessToken');
    }

    /**
     * Forgot password
     *
     * @param email
     */
    forgotPassword(email: string): Observable<any> {
        return this._httpClient.post('api/auth/forgot-password', email);
    }

    /**
     * Reset password
     *
     * @param password
     */
    resetPassword(password: string): Observable<any> {
        return this._httpClient.post('api/auth/reset-password', password);
    }

    /**
     * Sign in
     *
     * @param credentials
     */
    public signIn(credentials: { phone_number: string; password: string, member_id: string }): Observable<any> {
        if (this._authenticated) {
            return throwError(() => new Error('User is already logged in.'));
        }

        return this._http.post<string>(`/v1/member-client/sign-in-v2`, credentials)
            .pipe(
                mergeMap(
                    (response) => {
                        if (+response.code === HttpStatusCode.Ok) {
                            /* Store the access token in the local storage */
                            this.accessToken = response.data;

                            /* Set the authenticated flag to true */
                            this._authenticated = true;

                            /* Get the admin info */
                            return from(this._userService.getClientUserInfo()).pipe(
                                tap(userInfo => {
                                    this._userService.clientUserInfo = userInfo;
                                }),
                                map(() => response.data)
                            );
                        } else {
                            this._messageService.error(response.message);
                            return throwError(() => new Error(response.message));
                        }
                    })
            );
    }

    public register(credentials: any): Observable<any> {
        // Throw error, if the user is already logged in
        if (this._authenticated) {
            return throwError('User is already logged in.');
        }

        return this._http.post<string>(`/v1/member-client/register`, credentials)
            .pipe(
                switchMap(async (response) => {
                    if (+response.code == HttpStatusCode.Ok) {

                        /* Store the access token in the local storage */
                        this.accessToken = response.data;

                        /* Set the authenticated flag to true */
                        this._authenticated = true;

                        /* Get the admin info */
                        this._userService.clientUserInfo = await this._userService.getClientUserInfo();

                        /* Return the response */
                        return of(response.data);
                    }
                    this._messageService.error(response.message);
                    return throwError(response.message);
                })
            )
    }

    /**
     * Sign in using the access token
     */
    signInUsingToken(): Observable<any> {
        // Sign in using the token
        return this._httpClient.post('api/auth/sign-in-with-token', {
            accessToken: this.accessToken,
        }).pipe(
            catchError(() =>

                // Return false
                of(false),
            ),
            switchMap((response: any) => {
                // Replace the access token with the new one if it's available on
                // the response object.
                //
                // This is an added optional step for better security. Once you sign
                // in using the token, you should generate a new one on the server
                // side and attach it to the response object. Then the following
                // piece of code can replace the token with the refreshed one.
                if (response.accessToken) {
                    this.accessToken = response.accessToken;
                }

                // Set the authenticated flag to true
                this._authenticated = true;

                // Store the user on the user service
                this._userService.user = response.user;

                // Return true
                return of(true);
            }),
        );
    }

    /**
     * Sign out
     */
    public signOut(): Observable<any> {
        /* Remove the access token from the local storage */
        localStorage.removeItem('access_token');

        /* Set the authenticated flag to false */
        this._authenticated = false;

        /* Return the observable */
        return of(true);
    }

    /**
     * Check the authentication status
     */
    public check(): Observable<boolean> {
        /* If the user is already authenticated, then return true */
        if (this._authenticated) {
            return of(true);
        }

        /* If the access token doesn't exist, or it's expired, then return false */
        if (!this.accessToken) {
            return of(false);
        }

        /* If the access token exists, but it's expired, then return false */
        /*if (AuthUtils.isTokenExpired(this.accessToken)) {
            return of(false);
        }*/

        /* Try to refresh the access token */
        return this.refreshAccessToken()
    }

    /**
     * Refresh the access token
     */
    public refreshAccessToken(): Observable<any> {
        return this._http.get<{ token_head: string, token: string }>(`/v1/member-client/refresh-token`)
            .pipe(
                switchMap(async (response) => {
                    if (+response.code === HttpStatusCode.Ok) {

                        /**
                         * Replace the access token with the new one if it's available on the response object.
                         *
                         * This is an added optional step for better security. Once you sign
                         * in using the token, you should generate a new one on the server
                         * side and attach it to the response object. Then the following
                         * piece of code can replace the token with the refreshed one.
                         */
                        if (response.data.token) {
                            this.accessToken = response.data.token;
                        }

                        /* Set the authenticated flag to true */
                        this._authenticated = true;

                        /* Get the admin info */
                        this._userService.clientUserInfo = await this._userService.getClientUserInfo();

                        /* Return true */
                        return of(true);
                    }

                    return throwError(response.message);
                }),
                catchError(() => {
                    return of(false);
                })
            )
    }


    /**
     * Sign up
     *
     * @param user
     */
    signUp(user: { name: string; email: string; password: string; company: string }): Observable<any> {
        return this._httpClient.post('api/auth/sign-up', user);
    }

    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: { email: string; password: string }): Observable<any> {
        return this._httpClient.post('api/auth/unlock-session', credentials);
    }

}
